import { Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router';

import dayjs from 'dayjs';
import 'dayjs/locale/de';
import isToday from 'dayjs/plugin/isToday';
import 'swiper/css';

import '@bluecodecom/fonts';

import { LoaderProvider } from '@bluecodecom/loader';

import { ThemeProvider } from '@bluecodecom/theme';

import { nativeService } from '@bluecodecom/native';

import { trackingHooks } from '@bluecodecom/handyparken-webview/features/tracking';

import './i18n';

dayjs.extend(isToday);

const App = lazy(() => import('./app'));

const Main = () => {
  const {
    i18n: { language },
  } = useTranslation();
  useEffect(() => {
    nativeService.hideNavigation();
    nativeService.allowResumeMiniApp();
    dayjs.locale(language || 'de');
  }, [language]);

  useEffect(() => {
    // The native navigation can re-appear when the webview is sent to the
    // background and resumed (e.g. the user switches apps and comes back).
    // Re-hide it whenever the view becomes visible again so our custom
    // navigation stays in control.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        nativeService.hideNavigation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // App opened, NOT a screen view: this component is the shell around the
  // router, so it stays mounted for the whole session. Reporting it as
  // `screen_displayed` would put a phantom `main` step in front of every
  // deep-linked screen and corrupt the funnel.
  trackingHooks.useTrackEvent('Opened');

  return (
    <LoaderProvider>
      <ThemeProvider observe>
        <BrowserRouter>
          <Suspense>
            <App />
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    </LoaderProvider>
  );
};

createRoot(document.getElementById('root') as HTMLElement).render(<Main />);
